<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  fetchMarketplaceBlob,
  postMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

// Sekme sırası: önce bizim kestiklerimiz (ciro), sonra bize kesilenler (gider).
const SIRA = ['earchive', 'sale', 'despatch_out', 'purchase', 'despatch_in'];

const RENK = {
  purchase: { rozet: 'bg-red-100 text-red-800', bar: 'bg-red-500' },
  despatch_in: { rozet: 'bg-yellow-100 text-yellow-800', bar: 'bg-yellow-500' },
  earchive: { rozet: 'bg-green-100 text-green-800', bar: 'bg-green-500' },
  sale: { rozet: 'bg-violet-100 text-violet-800', bar: 'bg-violet-500' },
  despatch_out: { rozet: 'bg-woot-100 text-woot-800', bar: 'bg-woot-500' },
};
const rozet = s => RENK[s]?.rozet || 'bg-slate-100 text-slate-800';
const bar = s => RENK[s]?.bar || 'bg-slate-500';

const bugun = new Date().toISOString().slice(0, 10);
const gunOnce = n =>
  new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);
const ayBasi = () => `${bugun.slice(0, 7)}-01`;

const from = ref(gunOnce(90));
const to = ref(bugun);
const stream = ref('earchive'); // varsayılan: en yoğun akış
const q = ref('');
const cancelled = ref('');
const page = ref(1);
const perPage = ref(50);

const overview = ref(null);
const liste = ref(null);
const yukleniyor = ref(false);
const tazeleniyor = ref(false);
const hata = ref('');
const analizAcik = ref(false);
const sirket = ref('');
const sirketler = ref([]);

const tl = v =>
  (v == null ? 0 : v).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  });
const tarih = v => (v ? new Date(v).toLocaleDateString('tr-TR') : '—');
const saat = v =>
  v
    ? new Date(v).toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

const katalog = computed(() => overview.value?.catalog || {});
const sekmeler = computed(() =>
  SIRA.filter(k => katalog.value[k]).map(k => ({
    key: k,
    ...katalog.value[k],
    ...(overview.value?.streams?.[k] || {}),
  }))
);
const aktif = computed(() => katalog.value[stream.value] || {});
const gelenMi = computed(() => aktif.value.direction === 'gelen');

const sorgu = (extra = {}) => {
  const p = new URLSearchParams({ from: from.value, to: to.value, ...extra });
  if (sirket.value) p.set('company', sirket.value);
  if (stream.value) p.set('stream', stream.value);
  if (q.value.trim()) p.set('q', q.value.trim());
  if (cancelled.value) p.set('cancelled', cancelled.value);
  return p.toString();
};

const ozetGetir = async () => {
  const params = new URLSearchParams({ from: from.value, to: to.value });
  if (sirket.value) params.set('company', sirket.value);
  overview.value = await fetchMarketplaceJson(
    accountId,
    `nilvera_invoices/overview?${params.toString()}`
  );
  sirketler.value = overview.value?.companies || [];
  if (!sirket.value && overview.value?.company)
    sirket.value = overview.value.company;
};

const listeGetir = async () => {
  yukleniyor.value = true;
  hata.value = '';
  try {
    liste.value = await fetchMarketplaceJson(
      accountId,
      `nilvera_invoices/list?${sorgu({ page: page.value, per_page: perPage.value })}`
    );
  } catch (e) {
    hata.value = e.message || 'Liste alınamadı.';
  } finally {
    yukleniyor.value = false;
  }
};

const yenile = async () => {
  page.value = 1;
  await Promise.all([ozetGetir(), listeGetir()]);
};

const araligiSec = tur => {
  if (tur === 'buay') from.value = ayBasi();
  else if (tur === 'son30') from.value = gunOnce(30);
  else if (tur === 'son90') from.value = gunOnce(90);
  else from.value = `${bugun.slice(0, 4)}-01-01`;
  to.value = bugun;
  yenile();
};

const tazele = async () => {
  tazeleniyor.value = true;
  hata.value = '';
  try {
    await postMarketplaceJson(
      accountId,
      `nilvera_invoices/sync?days=45${sirket.value ? `&company=${sirket.value}` : ''}`
    );
    setTimeout(yenile, 8000);
  } catch (e) {
    hata.value = e.message || 'Nilvera taraması başlatılamadı.';
  } finally {
    setTimeout(() => {
      tazeleniyor.value = false;
    }, 8000);
  }
};

const indir = (blob, adi) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = adi;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

// Dosya adı sunucudan gelen kimlikle aynı: tür + dönem.
const dosyaAdi = uzanti => {
  const tur = (aktif.value.label || 'tum-belgeler')
    .toLocaleLowerCase('tr')
    .replace(/\s+/g, '-');
  return `${sirket.value || 'sirket'}_${tur}_${from.value}_${to.value}.${uzanti}`;
};

const disaAktar = async tur => {
  hata.value = '';
  try {
    const blob = await fetchMarketplaceBlob(
      accountId,
      `nilvera_invoices/export?${sorgu({ format_type: tur })}`
    );
    indir(blob, dosyaAdi(tur === 'csv' ? 'csv' : 'xls'));
  } catch (e) {
    hata.value = e.message || 'Dosya indirilemedi.';
  }
};

const belge = async (uuid, kind, no) => {
  hata.value = '';
  try {
    const blob = await fetchMarketplaceBlob(
      accountId,
      `nilvera_invoices/document/${uuid}?kind=${kind}`
    );
    if (kind === 'pdf') window.open(URL.createObjectURL(blob), '_blank');
    else indir(blob, `${no}.xml`);
  } catch (e) {
    hata.value = e.message || 'Belge açılamadı.';
  }
};

const aylik = computed(() => {
  const rows = (overview.value?.monthly || []).filter(
    r => r.stream === stream.value
  );
  const enBuyuk = Math.max(1, ...rows.map(r => Math.abs(r.total)));
  return rows.map(r => ({
    ...r,
    oran: Math.round((Math.abs(r.total) / enBuyuk) * 100),
  }));
});

const cariler = computed(() =>
  (overview.value?.top_counterparties || [])
    .filter(c => c.stream === stream.value)
    .slice(0, 6)
);

// Durum sütunu: Nilvera'nın ham metnini basmak yerine kısa Türkçe etiket.
// 15.09.2026'daki GİB kesintisinde bu alan 319 karakterlik İngilizce sistem
// hatası taşıdı ("EArsivWsPort is unavailable...") ve tabloyu okunmaz yaptı.
// Tam metin kaybolmuyor: hücrenin üzerine gelince ipucu olarak görünüyor.
const DURUM_ETIKET = {
  succeed: 'İletildi',
  waiting: 'Kuyrukta',
  error: 'İletilemedi',
  unknown: 'Belirsiz',
};
function durumEtiketi(f) {
  const etiket = DURUM_ETIKET[f.status];
  if (etiket) return etiket;
  const d = (f.status_detail || '').trim();
  if (!d) return f.status || '—';
  return d.length > 40 ? `${d.slice(0, 40)}…` : d;
}

const toplamSayfa = computed(() =>
  Math.max(1, Math.ceil((liste.value?.total || 0) / perPage.value))
);

watch([stream, cancelled, perPage, sirket], yenile);
watch(page, listeGetir);
onMounted(yenile);
</script>

<template>
  <div class="w-full h-full overflow-y-auto overflow-x-hidden bg-n-background">
    <div class="flex flex-col p-6 gap-5">
      <!-- Şirket geçişi: iki şirketin belgeleri ayrı defterlerdir, karışmaz.
           Tek şirket kuruluysa bu şerit hiç görünmez. -->
      <div
        v-if="sirketler.length > 1"
        class="flex flex-wrap items-center gap-2 p-2 rounded-xl bg-n-solid-1 border-2 border-n-weak"
      >
        <span class="px-2 text-sm font-semibold text-n-slate-11">ŞİRKET</span>
        <button
          v-for="c in sirketler"
          :key="c.key"
          class="h-11 px-5 rounded-lg text-base font-semibold border-2 transition"
          :class="
            sirket === c.key
              ? 'bg-woot-500 text-white border-woot-600 shadow'
              : 'bg-n-solid-1 text-n-slate-11 border-n-weak hover:border-n-strong'
          "
          @click="sirket = c.key"
        >
          {{ sirket === c.key ? '✓ ' : '' }}{{ c.label }}
        </button>
      </div>

      <!-- Başlık -->
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            e-Belgeler — Fatura, Arşiv, İrsaliye
            <span class="text-xs font-normal text-n-slate-10 align-middle ml-2">
              panel s3.1
            </span>
          </h1>
          <p class="text-sm text-n-slate-11 mt-1">
            Nilvera'daki tüm resmi belgeler türüne göre ayrı ayrı. Bu ekran
            yalnızca okur; buradan belge kesilmez.
            <span v-if="overview?.last_synced_at">
              · Son güncelleme {{ tarih(overview.last_synced_at) }}
              {{ saat(overview.last_synced_at) }}
            </span>
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-for="k in [
              { v: 'buay', l: 'Bu ay' },
              { v: 'son30', l: 'Son 30 gün' },
              { v: 'son90', l: 'Son 90 gün' },
              { v: 'yil', l: 'Bu yıl' },
            ]"
            :key="k.v"
            class="h-9 px-3 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-2"
            @click="araligiSec(k.v)"
          >
            {{ k.l }}
          </button>
          <input
            v-model="from"
            type="date"
            class="h-9 px-3 rounded-lg border border-n-strong bg-n-solid-1 text-n-slate-12"
          />
          <input
            v-model="to"
            type="date"
            class="h-9 px-3 rounded-lg border border-n-strong bg-n-solid-1 text-n-slate-12"
          />
          <button
            class="h-9 px-4 rounded-lg bg-woot-500 text-white font-medium hover:bg-woot-600"
            @click="yenile"
          >
            Getir
          </button>
          <button
            class="h-9 px-4 rounded-lg border border-n-strong text-n-slate-12 hover:bg-n-alpha-2"
            :disabled="tazeleniyor"
            @click="tazele"
          >
            {{ tazeleniyor ? 'Nilvera taranıyor…' : 'Nilvera’dan tazele' }}
          </button>
        </div>
      </div>

      <div
        v-if="hata"
        class="px-4 py-3 rounded-lg bg-red-50 text-red-700 border border-red-200"
      >
        {{ hata }}
      </div>

      <!-- Belge türü sekmeleri: her tür kendi kartı, kendi rakamı -->
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
        <button
          v-for="s in sekmeler"
          :key="s.key"
          class="text-left p-4 rounded-xl border-4 transition"
          :class="
            stream === s.key
              ? 'border-woot-500 bg-woot-50 shadow-lg ring-2 ring-woot-300'
              : 'border-n-weak bg-n-solid-1 hover:border-n-strong opacity-80'
          "
          @click="stream = s.key"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-semibold text-n-slate-12">{{
              s.label
            }}</span>
            <span
              class="text-[11px] px-2 py-0.5 rounded-full"
              :class="rozet(s.key)"
            >
              {{ s.direction }}
            </span>
          </div>
          <div class="text-2xl font-bold text-n-slate-12 mt-2">
            {{ tl(s.total) }}
          </div>
          <div class="text-xs text-n-slate-11 mt-1">
            {{ s.count || 0 }} belge · KDV {{ tl(s.tax) }}
            <span v-if="s.cancelled" class="text-red-600">
              · {{ s.cancelled }} iptal
            </span>
          </div>
        </button>
      </div>

      <!-- Seçili türün künyesi -->
      <div
        class="flex flex-wrap items-center gap-3 px-5 py-4 rounded-xl bg-woot-50 border-2 border-woot-200"
      >
        <span class="text-xl font-bold text-n-slate-12">
          {{ aktif.label }}
        </span>
        <span class="text-sm text-n-slate-11">{{ aktif.note }}</span>
        <span class="text-base font-semibold text-n-slate-12 ml-auto">
          {{ liste?.total || 0 }} belge · {{ from }} → {{ to }}
        </span>
        <button
          class="h-9 px-3 rounded-lg border border-n-strong text-sm text-n-slate-11 hover:bg-n-alpha-2"
          @click="analizAcik = !analizAcik"
        >
          {{ analizAcik ? 'Analizi gizle' : 'Analizi göster' }}
        </button>
      </div>

      <div v-if="analizAcik" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <!-- Aylık seyir (sadece seçili tür) -->
        <div class="p-5 rounded-xl border border-n-weak bg-n-solid-1">
          <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
            Aylık seyir — {{ aktif.label }}
          </h2>
          <div v-if="!aylik.length" class="text-sm text-n-slate-11">
            Bu dönemde kayıt yok.
          </div>
          <div
            v-for="a in aylik"
            :key="a.month"
            class="flex items-center gap-2 mb-2"
          >
            <span class="w-16 text-xs text-n-slate-11">{{ a.month }}</span>
            <div class="flex-1 h-4 bg-n-alpha-2 rounded overflow-hidden">
              <div
                class="h-full"
                :class="bar(a.stream)"
                :style="{ width: `${a.oran}%` }"
              />
            </div>
            <span class="w-24 text-xs font-medium text-n-slate-12 text-right">
              {{ tl(a.total) }}
            </span>
          </div>
        </div>

        <!-- Karşı taraf sıralaması -->
        <div
          class="lg:col-span-2 p-5 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
            {{
              gelenMi
                ? 'En çok fatura gelen firmalar'
                : 'En çok belge kesilen alıcılar'
            }}
          </h2>
          <div v-if="!cariler.length" class="text-sm text-n-slate-11">
            Bu dönemde kayıt yok.
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div
              v-for="c in cariler"
              :key="c.tax_number"
              class="flex items-center justify-between py-1.5 border-b border-n-weak"
            >
              <span class="text-sm text-n-slate-12 truncate mr-3">{{
                c.name
              }}</span>
              <span
                class="text-sm font-medium text-n-slate-12 whitespace-nowrap"
              >
                {{ tl(c.total) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filtreler + dışa aktarım -->
      <div class="flex flex-wrap items-center gap-2">
        <input
          v-model="q"
          type="search"
          :placeholder="`${aktif.label} içinde ara — ünvan, belge no veya VKN`"
          class="h-10 px-3 flex-1 min-w-72 rounded-lg border border-n-strong bg-n-solid-1 text-n-slate-12"
          @keyup.enter="yenile"
        />
        <select
          v-model="cancelled"
          class="h-10 px-3 rounded-lg border border-n-strong bg-n-solid-1 text-n-slate-12"
        >
          <option value="">İptaller dahil</option>
          <option value="false">Sadece geçerli</option>
          <option value="true">Sadece iptal</option>
        </select>
        <select
          v-model="perPage"
          class="h-10 px-3 rounded-lg border border-n-strong bg-n-solid-1 text-n-slate-12"
        >
          <option :value="50">Sayfada 50</option>
          <option :value="100">Sayfada 100</option>
          <option :value="200">Sayfada 200</option>
        </select>
        <button
          class="h-10 px-4 rounded-lg border border-n-strong text-n-slate-12 hover:bg-n-alpha-2"
          @click="disaAktar('xls')"
        >
          Excel: {{ aktif.label }}
        </button>
        <button
          class="h-10 px-4 rounded-lg border border-n-strong text-n-slate-12 hover:bg-n-alpha-2"
          @click="disaAktar('csv')"
        >
          CSV: {{ aktif.label }}
        </button>
      </div>

      <!-- Liste -->
      <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
        <div
          class="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-n-weak"
        >
          <span class="text-sm text-n-slate-11">
            <template v-if="liste?.summary">
              {{ liste.summary.count }} belge · matrah
              {{ tl(liste.summary.net) }} · KDV {{ tl(liste.summary.tax) }} ·
              toplam
              <strong class="text-n-slate-12">{{
                tl(liste.summary.total)
              }}</strong>
            </template>
          </span>
          <div v-if="toplamSayfa > 1" class="flex items-center gap-2">
            <button
              class="h-8 px-3 rounded border border-n-strong disabled:opacity-40"
              :disabled="page <= 1"
              @click="page -= 1"
            >
              Önceki
            </button>
            <span class="text-sm text-n-slate-11"
              >{{ page }} / {{ toplamSayfa }}</span
            >
            <button
              class="h-8 px-3 rounded border border-n-strong disabled:opacity-40"
              :disabled="page >= toplamSayfa"
              @click="page += 1"
            >
              Sonraki
            </button>
          </div>
        </div>

        <div v-if="yukleniyor" class="p-8 text-center text-n-slate-11">
          Yükleniyor…
        </div>
        <table v-else class="w-full text-sm">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="text-left font-medium px-4 py-3">Tarih</th>
              <th class="text-left font-medium px-4 py-3">Belge No</th>
              <th class="text-left font-medium px-4 py-3">
                {{ gelenMi ? 'Gönderen firma' : 'Alıcı' }}
              </th>
              <th class="text-left font-medium px-4 py-3">Tür</th>
              <th class="text-right font-medium px-4 py-3">Matrah</th>
              <th class="text-right font-medium px-4 py-3">KDV</th>
              <th class="text-right font-medium px-4 py-3">Toplam</th>
              <th class="text-left font-medium px-4 py-3">Durum</th>
              <th class="text-right font-medium px-4 py-3">Belge</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="f in liste?.invoices || []"
              :key="f.uuid"
              class="border-t border-n-weak hover:bg-n-alpha-1"
              :class="f.cancelled ? 'bg-red-50' : ''"
            >
              <td class="px-4 py-3 text-n-slate-12 whitespace-nowrap">
                {{ tarih(f.issue_date) }}
                <span class="block text-xs text-n-slate-10">{{
                  saat(f.issue_date)
                }}</span>
              </td>
              <td class="px-4 py-3 text-n-slate-12 font-mono text-xs">
                {{ f.number }}
              </td>
              <td class="px-4 py-3 text-n-slate-12">
                {{ f.counterparty }}
                <span class="block text-xs text-n-slate-10">{{
                  f.tax_number
                }}</span>
              </td>
              <td class="px-4 py-3">
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="rozet(f.stream)"
                >
                  {{ f.type }}
                </span>
              </td>
              <td class="px-4 py-3 text-right text-n-slate-12">
                {{ tl(f.net) }}
              </td>
              <td class="px-4 py-3 text-right text-n-slate-11">
                {{ tl(f.tax) }}
              </td>
              <td class="px-4 py-3 text-right font-semibold text-n-slate-12">
                {{ tl(f.total) }}
              </td>
              <td
                class="px-4 py-3 text-xs"
                :class="
                  f.cancelled ? 'text-red-600 font-medium' : 'text-n-slate-11'
                "
              >
                <span :title="f.status_detail || ''">{{
                  f.cancelled ? 'İPTAL' : durumEtiketi(f)
                }}</span>
              </td>
              <td class="px-4 py-3 text-right whitespace-nowrap">
                <button
                  class="text-woot-600 hover:underline mr-3"
                  @click="belge(f.uuid, 'pdf', f.number)"
                >
                  PDF
                </button>
                <button
                  class="text-n-slate-11 hover:underline"
                  @click="belge(f.uuid, 'xml', f.number)"
                >
                  XML
                </button>
              </td>
            </tr>
            <tr v-if="!(liste?.invoices || []).length">
              <td colspan="9" class="px-4 py-10 text-center text-n-slate-11">
                {{ from }} – {{ to }} aralığında {{ aktif.label }} kaydı yok.
              </td>
            </tr>
          </tbody>
        </table>
        <!-- Sayfalama tablonun ALTINDA da olmalı: 50 satır kaydırıp aşağı inen
             kullanıcı üstteki düğmeleri göremiyor ve "sayfalama yok" sanıyordu
             (2026-09-17 geri bildirimi). -->
        <div
          v-if="toplamSayfa > 1 && !yukleniyor"
          class="flex items-center justify-between gap-3 px-5 py-3 border-t border-n-weak"
        >
          <span class="text-sm text-n-slate-11">
            Toplam {{ liste?.total || 0 }} belge · sayfa başına {{ perPage }}
          </span>
          <div class="flex items-center gap-2">
            <button
              class="h-8 px-3 rounded border border-n-strong disabled:opacity-40"
              :disabled="page <= 1"
              @click="page -= 1"
            >
              Önceki
            </button>
            <span class="text-sm text-n-slate-11"
              >{{ page }} / {{ toplamSayfa }}</span
            >
            <button
              class="h-8 px-3 rounded border border-n-strong disabled:opacity-40"
              :disabled="page >= toplamSayfa"
              @click="page += 1"
            >
              Sonraki
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
